import { makeAutoObservable, runInAction } from 'mobx';
import axios from 'axios';
import { authStore } from './authStore';

// Get the API base URL from environment variable
const API_BASE_URL = import.meta.env.VITE_API_IOT || '';

// Types
export interface Device {
  device_id: string;
  relay?: number;
  area?: string;
  point?: string;
  pillarno?: string | number;
  lat?: number | string;
  lng?: number | string;
  status?: DeviceStatus;
  lights?: number;
  cpl?: number | string;
}

export interface DeviceStatus {
  s?: {
    csq?: number;
    nrg?: number;
    r1?: number;
    r2?: number;
    r3?: number;
    r1f?: number;
    r2f?: number;
    r3f?: number;
    lt?: string;
    time?: string;
  };
  sc?: {
    r1o?: string;
    r1c?: string;
    r2o?: string;
    r2c?: string;
    r3o?: string;
    r3c?: string;
  };
}

export interface SummaryData {
  total_consumption: number;
  latest_update: string;
}

class DeviceStore {
  devices: Device[] = [];
  deviceStatus: Record<string, DeviceStatus> = {};
  loading = false;
  error: string | null = null;
  isConnected = false;
  eventSource: EventSource | null = null;
  streamReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  summary: SummaryData | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Get authenticated API headers
  get headers() {
    return {
      headers: {
        Authorization: `Bearer ${authStore.accessToken}`
      }
    };
  }

  // Load all devices
  async loadDevices() {
    this.loading = true;
    this.error = null;

    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/devices`, this.headers);
      
      runInAction(() => {
        // Ensure response.data is an array before assigning
        if (!Array.isArray(response.data)) {
          console.error('API response is not an array:', response.data);
          this.error = 'Invalid data format received from server';
          this.devices = []; // Reset to empty array
        } else {
          this.devices = response.data;
          
          // Initialize device status
          this.devices.forEach(device => {
            if (device.status) {
              this.deviceStatus[device.device_id] = device.status;
            }
          });
        }
        
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to load devices';
        this.loading = false;
        
        if (error.response?.status === 401) {
          authStore.logout();
        }
      });
    }
  }

  // Load summary data
  async loadSummary() {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/summary`, this.headers);
      
      if (response.data.status === 'success') {
        runInAction(() => {
          this.summary = response.data.data;
        });
      }
    } catch (error) {
      console.error('Error loading summary data:', error);
    }
  }

  // Connect to SSE for real-time updates
  connectToSSE() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    
    if (this.streamReader) {
      this.streamReader.cancel();
      this.streamReader = null;
    }

    // First try with the standard EventSource approach
    this.connectWithEventSource();
  }

  // Connect using standard EventSource
  private connectWithEventSource() {
    try {
      // Create a URL object to handle the SSE connection
      const sseUrl = new URL(`${API_BASE_URL}/api/admin/devices/stream`);
      
      // Add the authorization token in the URL instead of using credentials
      if (authStore.accessToken) {
        sseUrl.searchParams.append('token', authStore.accessToken);
      }
      
      // Debug logging
      console.log('🔗 Attempting SSE connection to:', sseUrl.toString());
      console.log('🌐 Current origin:', window.location.origin);
      console.log('🔑 Has auth token:', !!authStore.accessToken);
      
      // EventSource has limited CORS support, so we need to be careful
      // The external server must be configured to allow the frontend's origin
      this.eventSource = new EventSource(sseUrl.toString());

      this.eventSource.onopen = () => {
        runInAction(() => {
          this.isConnected = true;
          this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
          console.log('SSE connection opened');
        });
      };

      this.eventSource.onmessage = (event) => {
        this.handleSSEMessage(event.data);
      };

      this.eventSource.onerror = (error) => {
        runInAction(() => {
          this.isConnected = false;
          console.error('❌ SSE connection error. This might be a CORS issue with the external IoT server.', error);
          console.error('🔍 Error details:', {
            readyState: this.eventSource?.readyState,
            url: sseUrl.toString(),
            origin: window.location.origin,
            type: error.type || 'unknown'
          });
        });
        
        // If it's likely a CORS issue, try the fetch-based fallback
        if (this.reconnectAttempts < 3) {
          this.reconnectSSE();
        } else {
          console.log('🔄 EventSource failed multiple times, trying fetch-based streaming...');
          this.connectWithFetchStream();
        }
      };
    } catch (error) {
      console.error('Error creating SSE connection:', error);
      this.connectWithFetchStream();
    }
  }

  // Alternative connection method using fetch streaming (can handle CORS better)
  private async connectWithFetchStream() {
    try {
      const sseUrl = new URL(`${API_BASE_URL}/api/admin/devices/stream`);
      
      if (authStore.accessToken) {
        sseUrl.searchParams.append('token', authStore.accessToken);
      }

      console.log('🚀 Attempting fetch-based SSE connection...');
      console.log('🔗 URL:', sseUrl.toString());

      const response = await fetch(sseUrl.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          ...(authStore.accessToken && { 'Authorization': `Bearer ${authStore.accessToken}` })
        },
        // Enable credentials for CORS
        credentials: 'include'
      });

      console.log('📡 Fetch response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      runInAction(() => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        console.log('Fetch-based SSE connection opened');
      });

      const reader = response.body.getReader();
      this.streamReader = reader; // Store for cleanup
      const decoder = new TextDecoder();
      let buffer = '';

      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              console.log('SSE stream ended');
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep the last incomplete line in buffer

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6); // Remove 'data: ' prefix
                if (data.trim()) {
                  this.handleSSEMessage(data);
                }
              }
            }
          }
        } catch (streamError) {
          console.error('Error reading SSE stream:', streamError);
          runInAction(() => {
            this.isConnected = false;
          });
          this.reconnectSSE();
        }
      };

      readStream();

    } catch (fetchError) {
      console.error('Fetch-based SSE connection failed:', fetchError);
      runInAction(() => {
        this.isConnected = false;
      });
      this.reconnectSSE();
    }
  }

  // Handle SSE message data (common for both EventSource and fetch approaches)
  private handleSSEMessage(data: string) {
    try {
      const update = JSON.parse(data);
      if (!update || !update.device_id || !update.status) {
        console.warn('Invalid update format:', update);
        return;
      }

      runInAction(() => {
        this.deviceStatus[update.device_id] = update.status;
        // Update device in the devices array if it exists
        const deviceIndex = this.devices.findIndex(d => d.device_id === update.device_id);
        if (deviceIndex >= 0) {
          this.devices[deviceIndex].status = update.status;
        }
      });
    } catch (error) {
      console.error('Error processing SSE update:', error);
    }
  }

  // Reconnect to SSE
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  reconnectSSE() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      const timeout = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      this.reconnectAttempts++;
      
      this.reconnectTimeout = setTimeout(() => {
        this.connectToSSE();
      }, timeout);
    }
  }

  // Get devices grouped by area
  get devicesByArea() {
    const areaGroups: Record<string, Device[]> = {};
    
    // Ensure devices is an array before using forEach
    if (!Array.isArray(this.devices)) {
      console.error('devices is not an array:', this.devices);
      return areaGroups; // Return empty result rather than causing an error
    }
    
    this.devices.forEach(device => {
      const area = (device.area || 'Unknown').trim();
      if (!areaGroups[area]) areaGroups[area] = [];
      areaGroups[area].push(device);
    });
    
    // Sort devices in each area
    Object.keys(areaGroups).forEach(area => {
      areaGroups[area].sort((a, b) => 
        (a.device_id || '').localeCompare(b.device_id || '', undefined, {numeric: true})
      );
    });
    
    return areaGroups;
  }

  // Send device command
  async sendCommand(deviceId: string, command: string) {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/device/command`, {
        device_id: deviceId,
        command
      }, this.headers);
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        authStore.logout();
      }
      throw error;
    }
  }

  // Update device schedule
  async updateSchedule(deviceId: string, relay: number, onTime: string, offTime: string) {
    // Format times from HH:MM to HHMM format
    const onTimeFormatted = onTime.replace(':', '');
    const offTimeFormatted = offTime.replace(':', '');
    
    const command = `cron_r${relay}o_${onTimeFormatted}_r${relay}c_${offTimeFormatted}`;
    return await this.sendCommand(deviceId, command);
  }

  // Get device schedules
  async getDeviceSchedules(deviceId: string) {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/schedules/compact?client_id=${deviceId}`, this.headers);
      
      // If successful, update device status
      if (response.data && typeof response.data === 'object') {
        return response.data;
      }
      
      return null;
    } catch (error: any) {
      console.error(`Error fetching schedules for ${deviceId}:`, error);
      if (error.response?.status === 401) {
        authStore.logout();
      }
      return null;
    }
  }

  // Update device status - useful for updating from external sources
  updateDeviceStatus(deviceId: string, status: Partial<DeviceStatus>) {
    // Get current status
    const currentStatus = this.deviceStatus[deviceId] || {};
    
    // Merge with new status
    this.deviceStatus[deviceId] = {
      ...currentStatus,
      ...status,
      // Merge nested objects
      s: { ...(currentStatus.s || {}), ...(status.s || {}) },
      sc: { ...(currentStatus.sc || {}), ...(status.sc || {}) }
    };
    
    // Update device in the devices array if it exists
    const deviceIndex = this.devices.findIndex(d => d.device_id === deviceId);
    if (deviceIndex >= 0) {
      this.devices[deviceIndex].status = this.deviceStatus[deviceId];
    }
  }

  // Force relay state
  async forceRelayState(deviceId: string, relay: number, state: boolean) {
    const command = state ? 
      `rl_${relay}_1_force` : 
      `rl_${relay}_0_force`;
      
    return this.sendCommand(deviceId, command);
  }

  // Time sync
  async syncTime(deviceId: string) {
    return this.sendCommand(deviceId, 'ntp_sync');
  }

  // Reboot device
  async rebootDevice(deviceId: string) {
    return this.sendCommand(deviceId, 'reboot');
  }

  // Cleanup
  cleanup() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    
    if (this.streamReader) {
      this.streamReader.cancel();
      this.streamReader = null;
    }
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    this.devices = []; // Explicitly reset to empty array
    this.deviceStatus = {};
    this.isConnected = false;
    this.error = null;
    this.loading = false;
  }
}

export const deviceStore = new DeviceStore(); 